import * as THREE from 'three';
import type { CharacterPackCache } from './character-pack-cache';
import { GEAR_LABELS, type GearSlot } from '../config/gear-defs';

/**
 * Worn gear, on the body (2026-09-04).
 *
 * User: "Now make worn gear actually render on the character."
 *
 * Forty-two pieces have existed since `b65361e`, every one of them fitted to
 * the medium rig and photographed from three angles by `tools/fit-check.py`,
 * and not one of them has ever appeared on a delver. The models were right and
 * nothing mounted them.
 *
 * ## Why a rigid attach and not skinning
 *
 * Every piece is authored **in rest-pose model space** — a greathelm's
 * vertices are at the height of the skull, a sabaton's are on the floor. That
 * is what made `fit-check.py` able to check anything at all, and it is what
 * makes this cheap: parent a piece to the bone that owns its part of the body,
 * cancel that bone's rest transform, and the piece sits exactly where it was
 * drawn and moves exactly as that limb moves.
 *
 * Skinning them would let a cuirass deform with the torso. It would also mean
 * weights, an armature per piece, and a Blender export path an order of
 * magnitude more complex — for a chunky low-poly pack whose own helmet and
 * cape are rigid attachments too. The pack's answer is the right one here.
 *
 * ## The two things this file exists to get right
 *
 * **A pair is two objects.** Gloves and boots ship as `<id>_l` and `<id>_r`
 * nodes in one file, because one mesh can only be parented to one bone and a
 * left glove flying around with the right hand is the whole of the bug.
 * `tools/kitlib.py`'s `split_lr` cuts them; this reassembles them onto two
 * bones.
 *
 * **The body's own gear has to get out of the way.** KayKit characters carry
 * their headgear and capes as ordinary submeshes of the character file —
 * `Knight_Helmet`, `Druid_Backpack`, `Barbarian_BearHat`. A Vault Greathelm
 * over the knight's own helmet is two helmets, and the one underneath pokes
 * through. So wearing a piece hides what it replaces.
 */

/**
 * Which bone carries each slot.
 *
 * Chosen for where the piece *hangs from*, not for where its centre is:
 *
 * - HEAD on `head`, so a helm turns when the delver looks.
 * - CHEST and BACK on `chest` — pauldrons read from the shoulders down, and a
 *   cape hangs off the upper back. Both should lead a torso turn, not lag it.
 * - WAIST and TRINKET on `hips`. A belt sits at z 0.70, which is the very base
 *   of `spine`; hanging it there would make a keyring swing with the ribcage.
 *   `hips` is the bone a belt is actually resting on.
 * - HANDS and FEET on both sides — see `split_lr`.
 *
 * Names are post-`sanitizeNodeName`: `GLTFLoader` strips `.` from every node
 * name because it is animation-track syntax, so the rig's `hand.l` is
 * queryable as `handl` and nothing else. That fact once cost this codebase
 * every weapon it tried to render; the comment in `character-visual.ts` marks
 * the grave.
 */
export const SLOT_BONES: Record<GearSlot, readonly string[]> = {
  HEAD: ['head'],
  CHEST: ['chest'],
  BACK: ['chest'],
  WAIST: ['hips'],
  HANDS: ['handl', 'handr'],
  FEET: ['footl', 'footr'],
  TRINKET: ['hips'],
};

/**
 * What a worn piece switches off, matched on the tail of a submesh name.
 *
 * A suffix rule rather than a list of eight characters' worth of mesh names:
 * every pack in this family names the part and prefixes the character
 * (`Knight_Helmet`, `Mage_Hat`, `RogueHooded_Mask`), so the suffix *is* the
 * part. A new class added to the roster is covered without an edit here, which
 * is the only kind of table worth writing.
 *
 * `_Head` is deliberately absent. That is the face.
 */
export const HIDDEN_BY_SLOT: Partial<Record<GearSlot, readonly string[]>> = {
  HEAD: ['_Helmet', '_HelmetVisor', '_BearHat', '_Hat', '_Goggles', '_Mask'],
  CHEST: ['_ShoulderpadLeft', '_ShoulderpadRight', '_BearPelt'],
  BACK: ['_Cape', '_Backpack', '_Quiver', '_BearPelt'],
};

/**
 * Chest pieces that *are* a torso, and so take the wearer's own off (2026-10-15).
 *
 * QA: "the extra chestplate pokes through the skin's own chest". These three
 * are built on `Knight_Body` itself (`_torso()` in `tools/make-armour.py`), so
 * on a knight the piece lay exactly on top of his body — coplanar, the two
 * fighting for every pixel, and with a skin on the body that fight was red
 * against grey. On any other class it was worse: a rogue's tunic is a knight's
 * torso give or take 8cm in places, so green broke through the plate in
 * patches. Pushing the plate outward far enough to clear every class would make
 * the knight a barrel. The piece is a body, so it replaces the body, the same
 * way a helm replaces the helmet. Pelts and mantles sit *on* the shoulders and
 * are not here.
 */
export const REPLACES_BODY: ReadonlySet<string> = new Set(['banded_harness', 'warden_pauldrons', 'vault_cuirass']);

/** True when this character submesh is replaced by something in one of the worn slots. */
export function isHiddenBy(meshName: string, slots: readonly GearSlot[], defIds: readonly string[] = []): boolean {
  if (meshName.endsWith('_Body') && defIds.some((id) => REPLACES_BODY.has(id))) return true;
  return slots.some((slot) => (HIDDEN_BY_SLOT[slot] ?? []).some((suffix) => meshName.endsWith(suffix)));
}

/** Every atlas a gear piece is textured from — the image names inside `gear/*.gltf`. */
const GEAR_ATLASES = ['knight_texture', 'barbarian_texture', 'druid_texture'] as const;

/**
 * The texture key a gear material swaps to under a skin, or null when it keeps its own.
 *
 * Keyed on the material's *atlas*, not on the wearer's class: a helm is drawn
 * on the knight atlas whoever wears it, so handing it a rogue's skin atlas
 * would sample the wrong layout. `tools/recolor-skin.py --gear` puts each atlas
 * through the skin's own transform, and this names the result.
 */
export function gearSkinKey(skinAssetKey: string | undefined, atlasName: string | undefined): string | null {
  if (!skinAssetKey || !atlasName) return null;
  const atlas = GEAR_ATLASES.find((a) => a === atlasName);
  return atlas ? `gear/${skinAssetKey}.${atlas.replace('_texture', '')}` : null;
}

/**
 * Where a piece has to sit, expressed in the bone's own frame.
 *
 * The piece is authored in the character's rest-pose model space, so the
 * transform that keeps it there while making it a child of `bone` is exactly
 * the one that cancels the bone out:
 *
 *     local = bone.matrixWorld⁻¹ · character.matrixWorld · piece
 *
 * Read right to left: take the piece where it was drawn, lift it into world
 * space through the character, then undo the bone. Every term but `piece`
 * cancels at rest, and once the bone moves the piece moves with it — which is
 * the entire trick.
 *
 * Pure, and separated from the attaching so it can be tested against matrices
 * instead of against a loaded glTF and a GPU.
 */
export function boneLocalMatrix(
  boneWorld: THREE.Matrix4,
  characterWorld: THREE.Matrix4,
  pieceInCharacterSpace: THREE.Matrix4,
): THREE.Matrix4 {
  return new THREE.Matrix4()
    .copy(boneWorld).invert()
    .multiply(characterWorld)
    .multiply(pieceInCharacterSpace);
}

/**
 * The nodes to hang, and where.
 *
 * A single-object piece attaches to the slot's one bone. A pair looks for
 * `<id>_l` and `<id>_r` and attaches each to its own side. A file that carries
 * neither shape — one node where two bones were expected — is skipped rather
 * than guessed at: half a pair of boots on one foot is a worse outcome than
 * none, and it would look like a physics bug rather than a missing file.
 */
export function nodesForSlot(scene: THREE.Object3D, defId: string, slot: GearSlot): { node: THREE.Object3D; bone: string }[] {
  const bones = SLOT_BONES[slot];
  if (bones.length === 1) {
    const node = scene.getObjectByName(defId) ?? scene.children[0];
    return node ? [{ node, bone: bones[0] }] : [];
  }
  const out: { node: THREE.Object3D; bone: string }[] = [];
  for (const [index, side] of ['_l', '_r'].entries()) {
    const node = scene.getObjectByName(defId + side);
    if (node) out.push({ node, bone: bones[index] });
  }
  // Both halves or neither.
  return out.length === bones.length ? out : [];
}

/**
 * Hang every worn piece on `character`, and switch off what they replace.
 *
 * Called once, at load, before any clip plays: the bone matrices read here are
 * the rest pose, which is the pose every piece was authored against.
 *
 * A piece that fails to load costs the player that piece and nothing else —
 * the same trade `applySkin` makes, and for the same reason. A delver missing
 * a belt is a delver; a delver who failed to render is a hole in the room.
 */
export async function attachWornGear(
  character: THREE.Object3D,
  defIds: readonly string[],
  cache: CharacterPackCache,
  skinAssetKey?: string,
): Promise<void> {
  const wearing = defIds
    .map((id) => ({ id, slot: GEAR_LABELS[id]?.slot }))
    .filter((w): w is { id: string; slot: GearSlot } => w.slot !== undefined);
  if (wearing.length === 0) return;

  // Hidden before anything is attached, so a piece that fails to load does not
  // leave the delver bareheaded — it leaves them in the helmet they came with.
  const loaded = await Promise.all(wearing.map(async (w) => {
    try {
      return { ...w, scene: await cache.loadGearModel(w.id) };
    } catch (err) {
      console.warn(`[worn-gear] failed to load "${w.id}"; that slot renders bare`, err);
      return null;
    }
  }));

  const worn = loaded.filter((w): w is NonNullable<typeof w> => w !== null);
  const slots = worn.map((w) => w.slot);
  const ids = worn.map((w) => w.id);
  character.traverse((obj) => {
    if ((obj as THREE.Mesh).isMesh && isHiddenBy(obj.name, slots, ids)) obj.visible = false;
  });

  const skinMaps = skinAssetKey ? await loadGearSkinMaps(worn.map((w) => w.scene), skinAssetKey, cache) : null;

  character.updateMatrixWorld(true);
  let helm: { id: string; node: THREE.Object3D } | null = null;
  for (const piece of worn) {
    const clone = piece.scene.clone(true);
    if (skinMaps) applyGearSkin(clone, skinMaps);
    clone.updateMatrixWorld(true);
    for (const { node, bone } of nodesForSlot(clone, piece.id, piece.slot)) {
      if (piece.slot === 'HEAD') helm = { id: piece.id, node };
      const target = character.getObjectByName(bone);
      if (!target) continue;
      const local = boneLocalMatrix(target.matrixWorld, character.matrixWorld, node.matrixWorld);
      target.add(node);
      // Set outright rather than decomposed into position/quaternion/scale:
      // with `matrixAutoUpdate` on, three.js recomposes the matrix from those
      // three every frame, so the one thing this file computes would be thrown
      // away sixty times a second.
      node.matrixAutoUpdate = false;
      node.matrix.copy(local);
      node.matrixWorldNeedsUpdate = true;
    }
  }
  if (helm) {
    character.updateMatrixWorld(true);
    tuckUnderHelm(character, helm.node, helm.id);
  }
}

/**
 * How far inside a helm's wall a tucked vertex ends up, as a fraction of the
 * wall's distance from the helm's centre. Far enough that depth precision can
 * never pick the hair over the plate; near enough that nothing shrinks visibly.
 */
export const TUCK_INSIDE = 0.95;

/** Tucked head geometry, per source geometry and per helm — every delver of a class shares the same head. */
const tuckedHeads = new WeakMap<THREE.BufferGeometry, Map<string, THREE.BufferGeometry>>();

/**
 * Pull whatever of the wearer's head pokes through a helm back inside it (2026-10-15).
 *
 * QA: "from behind the helmet looks split into separate segments". Every helm
 * is the knight's own shell, drawn round the knight's own head — and the other
 * classes carry their hair, hood and antlers in the same `_Head` mesh as the
 * face, so none of it can be switched off the way `Knight_Helmet` is. A rogue's
 * hair sits up to a third further out than the knight's skull, so from behind
 * it broke through the plate in brown patches, and the helm read as plates
 * with gaps between them. Scaling the helm to clear the worst class would put
 * the knight in a bucket.
 *
 * So the head is fitted to the helm instead: a ray from the helm's centre
 * through each head vertex, and a vertex that lies on or past the first wall
 * it meets is moved to just inside that wall. A vertex whose ray meets no wall
 * — the face, under the open front; hair hanging below the rim — is never
 * touched, so nothing that was visible before changes except what was poking
 * through. Done in rest pose and on the bind-space geometry, so the head still
 * skins exactly as before. Split vertices share a position and so move
 * identically: the low-poly seams cannot open.
 */
export function tuckUnderHelm(character: THREE.Object3D, helm: THREE.Object3D, helmId: string): void {
  const shells: THREE.Mesh[] = [];
  helm.traverse((obj) => { if ((obj as THREE.Mesh).isMesh) shells.push(obj as THREE.Mesh); });
  if (shells.length === 0) return;
  const centre = new THREE.Box3().setFromObject(helm).getCenter(new THREE.Vector3());

  character.traverse((obj) => {
    const head = obj as THREE.Mesh;
    if (!head.isMesh || !head.name.endsWith('_Head')) return;
    let fitted = tuckedHeads.get(head.geometry);
    if (!fitted) tuckedHeads.set(head.geometry, fitted = new Map());
    let geometry = fitted.get(helmId);
    if (!geometry) {
      geometry = tuckedGeometry(head, shells, centre);
      fitted.set(helmId, geometry);
    }
    head.geometry = geometry;
  });
}

function tuckedGeometry(head: THREE.Mesh, shells: THREE.Mesh[], centre: THREE.Vector3): THREE.BufferGeometry {
  const geometry = head.geometry.clone();
  const position = geometry.getAttribute('position') as THREE.BufferAttribute;
  const toLocal = head.matrixWorld.clone().invert();
  const raycaster = new THREE.Raycaster();
  const vertex = new THREE.Vector3();
  const direction = new THREE.Vector3();
  // One answer per distinct position: split vertices repeat it, and the
  // raycast is the expensive part.
  const done = new Map<string, THREE.Vector3 | null>();
  for (let i = 0; i < position.count; i++) {
    const key = `${position.getX(i)},${position.getY(i)},${position.getZ(i)}`;
    let moved = done.get(key);
    if (moved === undefined) {
      moved = null;
      vertex.fromBufferAttribute(position, i).applyMatrix4(head.matrixWorld);
      direction.subVectors(vertex, centre);
      const reach = direction.length();
      if (reach > 1e-4) {
        direction.divideScalar(reach);
        raycaster.set(centre, direction);
        raycaster.far = reach / TUCK_INSIDE;
        const wall = raycaster.intersectObjects(shells, false)[0];
        if (wall) {
          moved = centre.clone().addScaledVector(direction, wall.distance * TUCK_INSIDE).applyMatrix4(toLocal);
        }
      }
      done.set(key, moved);
    }
    if (moved) position.setXYZ(i, moved.x, moved.y, moved.z);
  }
  position.needsUpdate = true;
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

/**
 * Every recoloured atlas these pieces need under `skinAssetKey`, by source atlas name.
 *
 * Loaded once per call rather than per material, and an atlas that fails to
 * load is simply left out — that piece keeps its own colours, the same trade
 * the body makes when its skin texture is missing.
 */
async function loadGearSkinMaps(
  scenes: readonly THREE.Object3D[],
  skinAssetKey: string,
  cache: CharacterPackCache,
): Promise<Map<string, THREE.Texture>> {
  const atlases = new Set<string>();
  for (const scene of scenes) {
    scene.traverse((obj) => {
      for (const material of materialsOf(obj)) {
        const name = (material as THREE.MeshStandardMaterial).map?.name;
        if (gearSkinKey(skinAssetKey, name)) atlases.add(name!);
      }
    });
  }
  const maps = new Map<string, THREE.Texture>();
  await Promise.all([...atlases].map(async (atlas) => {
    const key = gearSkinKey(skinAssetKey, atlas)!;
    try {
      maps.set(atlas, await cache.loadSkinTexture(key));
    } catch (err) {
      console.warn(`[worn-gear] no "${key}" atlas; that gear keeps its own colours`, err);
    }
  }));
  return maps;
}

/**
 * Swap a cloned piece's atlases for the skin's.
 *
 * `Object3D.clone` shares materials with the cached original, so each one is
 * cloned before its map changes — otherwise the first crimson knight to load
 * would turn every other delver's copy of that helm crimson too.
 *
 * The skin texture is cloned as well, to carry the original map's sampler: the
 * ramp atlas is drawn NEAREST so its swatches stay flat, while the pack atlas
 * is filtered. A clone shares its image source, so this costs a texture object
 * and not a second upload.
 */
function applyGearSkin(piece: THREE.Object3D, maps: ReadonlyMap<string, THREE.Texture>): void {
  const cloned = new Map<THREE.Material, THREE.Material>();
  piece.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh) return;
    const swap = (material: THREE.Material): THREE.Material => {
      const original = (material as THREE.MeshStandardMaterial).map;
      const skin = original ? maps.get(original.name) : undefined;
      if (!original || !skin) return material;
      let clone = cloned.get(material);
      if (!clone) {
        const map = skin.clone();
        map.magFilter = original.magFilter;
        map.minFilter = original.minFilter;
        map.wrapS = original.wrapS;
        map.wrapT = original.wrapT;
        map.needsUpdate = true;
        clone = material.clone();
        (clone as THREE.MeshStandardMaterial).map = map;
        cloned.set(material, clone);
      }
      return clone;
    };
    mesh.material = Array.isArray(mesh.material) ? mesh.material.map(swap) : swap(mesh.material);
  });
}

function materialsOf(obj: THREE.Object3D): THREE.Material[] {
  const mesh = obj as THREE.Mesh;
  if (!mesh.isMesh) return [];
  return Array.isArray(mesh.material) ? mesh.material : [mesh.material];
}
